import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { debounce } from 'lodash';
import {
	TableFilters,
	TableFilterType,
	Padding,
	Input,
	Flex,
} from '@zeal/web-ui';
import { useTranslation } from 'react-i18next';
import { useQueryString } from '@zeal/zeal-lib';

export interface ResourceFiltersProps {
	filters?: TableFilterType;
	hasSearch?: boolean;
	searchPlaceholder?: string;
}
export const ResourceFilters = ({
	filters,
	hasSearch,
	searchPlaceholder,
}: ResourceFiltersProps) => {
	const { t } = useTranslation('common');
	const [search, setSearch] = useState('');

	const [queryState, updateQuery] = useQueryString();
	const { keywords, page } = queryState;

	const updateFiltersQueries = useCallback(
		({ keywords }) => {
			updateQuery(
				{
					...(keywords && { keywords }),
					page,
				},
				{ noMerge: true }
			);
		},
		[updateQuery, page]
	);

	const updateSearchQuery = useMemo(
		() =>
			debounce((keywords) => {
				updateFiltersQueries({ keywords });
			}, 500),
		[updateFiltersQueries]
	);

	const onSearchChange = useCallback(
		(e) => {
			setSearch(e.target.value);
			updateSearchQuery(e.target.value);
		},
		[updateSearchQuery]
	);

	const searchMarkup = hasSearch && (
		<SearchWrapper>
			<Input
				type="text"
				endIconProps={{
					iconName: 'search',
				}}
				placeholder={searchPlaceholder || `${t('search_placeholder')}`}
				onChange={onSearchChange}
				value={search}
			/>
		</SearchWrapper>
	);

	const filtersMarkup = filters && <TableFilters filters={filters} />;

	useEffect(() => {
		setSearch(keywords);
	}, []);

	if (!hasSearch && !searchMarkup && !filtersMarkup) {
		return null;
	}

	return (
		<Padding
			padding={{
				all: 'lg',
			}}
		>
			<Flex isColumn gap="md">
				{searchMarkup}
				{filtersMarkup}
			</Flex>
		</Padding>
	);
};
const SearchWrapper = styled.div`
	max-width: 40rem;
`;
